import { useDependencyInjector, useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import React, { useCallback, useMemo, useRef } from "react";
import { SearchMessagesSection } from "../../components/SearchMessagesSection";
import classnames from "classnames";
import { EntityFilter, FilterSettingsType } from "@whatslly/ui-components/EntityFilter";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { ChatMessagesStore } from "../../stores/ChatMessagesStore";
import { LOADER_IMG } from "@whatslly/commons/constants";
import { useTranslation } from "react-i18next";
import { Messages } from "../../components/Messages";
import { ScrollToBottomButton } from "../../components/ScrollToBottomButton";
import { Footer } from "../../components/Footer";
import { observer } from "mobx-react-lite";
import styles from "../../ChatComponent.module.scss";
import { MessagesTabsLocalStore } from "../../stores/MessagesTabsLocalStore";
import { ErrorBox } from "@whatslly/ui-components/ErrorBox";
import { ChatDateFilter } from "../../../components/ChatDateFilter";
import { Dayjs } from "dayjs";
import type { MessageFloatingActionType } from "../../components/Message/Components/FloatingActions";
import { MessageInlineActionType } from "@whatsllyorg/crm-components/ChatComponent/components/Message/Components/InlineActions";
import { MenuDownloadButton } from "../components/menu-download-button";
import { ChatEntityFilterButton } from "../components/ChatEntityFilterButton";

export const PersonalTab: React.FC<{
  hideDateFilter?: boolean;
  filtersSettings?: FilterSettingsType[];
  FilterComponent?: React.FC;
  enableDateRangeFilter?: boolean;
  allowedToDownload?: boolean;
  floatingActions?: [MessageFloatingActionType] | [MessageFloatingActionType, MessageFloatingActionType];
  isSidecarsComponent?: boolean;
  inlineActions?: MessageInlineActionType[];
  openCalendarInTheMiddle?: boolean;
  limitDateRange?: number;
}> = observer(
  ({
    hideDateFilter,
    filtersSettings,
    FilterComponent,
    enableDateRangeFilter,
    allowedToDownload,
    isSidecarsComponent,
    floatingActions,
    inlineActions,
    openCalendarInTheMiddle,
    limitDateRange,
  }) => {
    const scrollToBottomRef = useRef<{ scrollBottom: () => void } | null>(null);
    const messageContainerRef = useRef<HTMLDivElement>(null);
    const localStore = useLocalStoreWithInjectedDependencies(MessagesTabsLocalStore);
    const messagesStore = useDependencyInjector(ChatMessagesStore);
    const { t } = useTranslation();

    const dateRangeConfigs = useMemo(() => {
      if (!enableDateRangeFilter) return {};
      return {
        onDateRangeChange: (dateRange: { fromDate: Dayjs | null; toDate: Dayjs | null }) => messagesStore.setDateRange(dateRange),
        onFilterTypeChange: (mode: "byMonth" | "range") => messagesStore.setDateFilterMode(mode),
        dateRange: messagesStore.dateRange,
        openInMode: messagesStore.dateFilterMode,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableDateRangeFilter, messagesStore, messagesStore.dateRange]);

    const setShowScrollDown = useCallback(
      (show: boolean) => {
        localStore.setShowScrollToBottom(show);
      },
      [localStore]
    );

    const selectAllMessages = useCallback(() => localStore.setSelectAllMessages("personal"), [localStore]);

    return (
      <>
        <SearchMessagesSection
          isLoading={messagesStore.disableDeleteButton}
          messages={messagesStore.messagesFiltered.state === "fulfilled" ? messagesStore.messagesFiltered.value : []}
          onSearchTextChange={(newText) => localStore.setSearchText(newText)}
          onFocusMessageChange={(msgId) => localStore.setFocusMessageId(msgId)}
        >
          <div className={classnames(styles.headerButtons)}>
            {messagesStore.allowedToDelete && (
              <div
                data-testid="delete-personal-message-button"
                onClick={() => localStore.toggleDeleteMode()}
                className={classnames(styles.headerButton, {
                  [styles.selected]: localStore.isDeleteMode,
                  [styles.disabled]: messagesStore.disableDeleteButton,
                })}
              >
                <SvgV2 width={12} height={12} iconName="trash" />
              </div>
            )}

            {allowedToDownload && (
              <MenuDownloadButton
                isDownloadMode={localStore.isDownloadMode}
                toggleDownloadMode={localStore.toggleDownloadMode.bind(localStore)}
                disabled={messagesStore.messages.state !== "fulfilled" || messagesStore.userId.state !== "fulfilled"}
              />
            )}
            <div
              data-testid="refresh-personal-message-button"
              onClick={messagesStore.refreshMessages}
              className={classnames(styles.headerButton, { [styles.disabled]: messagesStore.disableHeaderButton })}
            >
              <SvgV2 width={15} height={15} iconName="synchronize" />
            </div>
            {FilterComponent ? (
              <FilterComponent />
            ) : (
              <EntityFilter
                textEllipsis={true}
                collapsibleMenuClassName={styles.collapsibleMenu}
                initFilteredFields={messagesStore.getFilters()}
                title={t("chat_component.filters_title")}
                currentUserId={messagesStore.currentUserId}
                filterSettings={
                  filtersSettings || [
                    {
                      title: t("chat_component.synced_by"),
                      searchKey: `ownerId`,
                      searchLabel: `ownerName`,
                      maskMe: true,
                    },
                    {
                      title: t("chat_component.sent_by"),
                      searchKey: `senderId`,
                      searchLabel: `senderName`,
                      maskMe: true,
                    },
                    {
                      title: t("chat_component.received_by"),
                      searchKey: `receiverId`,
                      searchLabel: `receiverName`,
                      maskMe: true,
                    },
                  ]
                }
                entities={messagesStore.currentEntities}
                EntityFilterButton={ChatEntityFilterButton}
                onSelectedFiltersChanged={(selected) => {
                  messagesStore.setFilters(selected);
                }}
              />
            )}
            {!hideDateFilter && (
              <ChatDateFilter
                limitDateRange={limitDateRange}
                isSideCarFilter={isSidecarsComponent}
                defaultValue={messagesStore.showXMonthsAgo}
                disabled={messagesStore.messages.state !== "fulfilled" || messagesStore.userId.state !== "fulfilled"}
                onChange={(value) => messagesStore.setShowLastXMonthsTimeStamp(value)}
                openCalendarInTheMiddle={openCalendarInTheMiddle}
                {...dateRangeConfigs}
              />
            )}
          </div>
        </SearchMessagesSection>
        <div className={styles.messagesWrapper}>
          <div ref={messageContainerRef} className={styles.messageContainer} data-testid="messages-preview">
            {messagesStore.messagesFiltered.case({
              pending: () => {
                return (
                  <div className={styles.noMessages} data-testid={"personal-tab-loader"}>
                    <img src={LOADER_IMG} alt="loader" />
                    <div>{t("chat_component.loading")}</div>
                  </div>
                );
              },
              rejected: (error) => {
                return (
                  <div data-testid="error-load-messages" className={classnames(styles.noMessages, styles.errorMessage)}>
                    <ErrorBox error={error.message} />
                  </div>
                );
              },
              fulfilled: (items) => {
                return (
                  <Messages
                    ref={scrollToBottomRef}
                    crm={messagesStore.crm}
                    setShowScrollDown={setShowScrollDown}
                    items={items}
                    tempDeletedMessages={localStore.tempDeletedMessages}
                    isSelectMessagesMode={localStore.isDeleteMode || localStore.isDownloadMode}
                    selectedMessages={localStore.selectedMessages}
                    searchText={localStore.searchText}
                    onChangeSelectMessageCheckbox={(messageId) => localStore.setSelectedMessages(messageId)}
                    highlightedMsgId={localStore.focusMessageId}
                    setHighlightedMessageId={(msgId) => localStore.setFocusMessageId(msgId)}
                    getEntityUrl={(name, id) => messagesStore.getEntityUrl(name, id)}
                    floatingActions={floatingActions}
                    inlineActions={inlineActions}
                    hideMessageAck={true}
                  />
                );
              },
            })}
          </div>
          {messagesStore.messages.state === "fulfilled" && messagesStore.messages.value.length > 0 && (
            <ScrollToBottomButton showScrollToBottom={localStore.showScrollToBottom} scrollToBottomRef={scrollToBottomRef} />
          )}
        </div>
        {localStore.isDeleteMode && (
          <Footer
            mode={"delete"}
            closeDeleteMode={localStore.closeDeleteMode.bind(localStore)}
            numOfSelectedMessages={localStore.selectedMessages.size}
            selectAll={selectAllMessages}
            clickOnDeleteMessages={localStore.deleteMessages.bind(localStore)}
          />
        )}
        {localStore.isDownloadMode && (
          <Footer
            mode={"download"}
            closeDeleteMode={localStore.closeDownloadMode.bind(localStore)}
            numOfSelectedMessages={localStore.selectedMessages.size}
            selectAll={selectAllMessages}
            downloadMessages={localStore.downloadMessages.bind(localStore)}
          />
        )}
      </>
    );
  }
);
